import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import portalAdminService from '../../services/portalAdminService';
import PortalShell from './PortalShell';
import { Avatar, KeyIcon, initials } from './PortalBits';

export default function PortalMembersDirectoryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('all');
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resume, setResume] = useState(null); // member whose Portal popup is open

  // Portal Noir is small (≤144), so fetch everyone once and filter on the client.
  useEffect(() => {
    portalAdminService.listMembers({ pageSize: 200 }).then((r) => {
      if (r.success) setAllMembers(r.data || []);
      setLoading(false);
    });
  }, []);

  // Only cities that actually have members populate the filter.
  const memberCities = Array.from(new Set(allMembers.map((m) => m.location).filter(Boolean))).sort((a, b) => a.localeCompare(b));

  const q = search.trim().toLowerCase();
  const rows = allMembers.filter((m) => {
    const matchesSearch = !q || `${m.displayName || ''} ${m.handle || ''} ${m.location || ''}`.toLowerCase().includes(q);
    const matchesCity = city === 'all' || m.location === city;
    return matchesSearch && matchesCity;
  });

  return (
    <PortalShell viewerState="accepted">
      <div className="wrap" style={{ paddingTop: 'clamp(40px,7vw,80px)', paddingBottom: 100 }}>
        <section className="hero enter" style={{ marginBottom: 50 }}>
          <span className="kicker">MEMBERS</span>
          <h1 className="display" style={{ fontSize: 'clamp(52px,8vw,108px)', marginTop: 20 }}>The crew<span className="dot">.</span></h1>
          <p className="lede" style={{ marginTop: 22, maxWidth: '42ch' }}>
            Tap a name to learn more about them.
          </p>
        </section>

        <div className="toolbar enter enter-2">
          <input className="input" placeholder="Find a name or @handle…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="select" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="all">All cities</option>
            {memberCities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="enter enter-3" style={{ marginTop: 8 }}>
          {loading ? (
            <p className="meta" style={{ padding: '20px 6px', color: 'var(--cream-faint)' }}>LOADING…</p>
          ) : rows.length === 0 ? (
            <p className="lede" style={{ padding: '20px 6px', color: 'var(--cream-faint)' }}>The directory is small. Help us grow it — invite the ones you love.</p>
          ) : (
            rows.map((m) => (
              <div className="mrow" key={m.userId} onClick={() => setResume(m)}>
                <Avatar src={m.avatarUrl} init={initials(m.displayName)} size="lg" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mrow__name">{m.displayName}</div>
                  <div className="mrow__sub">
                    {m.handle ? `@${m.handle}` : ''}{m.location ? ` · ${m.location}` : ''}{(m.portalRole === 'owner') ? ' · OWNER' : (m.portalRole === 'admin' ? ' · ADMIN' : '')}
                  </div>
                </div>
                {m.keyTier === 'key_holder'
                  ? <KeyIcon title="Key holder · unlimited keys" />
                  : <span className="badge badge--plain">5 KEYS</span>}
              </div>
            ))
          )}
        </div>
      </div>

      {resume && (
        <ResumeModal
          m={resume}
          onClose={() => setResume(null)}
          onOpenFull={() => navigate(resume.handle ? `/${resume.handle}` : `/users/${resume.userId}`)}
        />
      )}
    </PortalShell>
  );
}

function ResumeModal({ m, onClose, onOpenFull }) {
  useEffect(() => {
    const k = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const since = m.memberSince ? `’${String(new Date(m.memberSince).getFullYear()).slice(2)}` : '—';

  return (
    <div className="portal-modal" onClick={onClose}>
      <div className="resume enter" onClick={(e) => e.stopPropagation()}>
        <div className="resume__top">
          <span className="kicker kicker--gold">PORTAL NOIR</span>
          <button className="resume__x" onClick={onClose}>×</button>
        </div>
        <div className="resume__id">
          <Avatar src={m.avatarUrl} init={initials(m.displayName)} size="lg" />
          <div>
            <div className="resume__name">{m.displayName}</div>
            <div className="meta" style={{ marginTop: 7 }}>{m.handle ? `@${m.handle}` : ''}{m.location ? ` · ${m.location}` : ''}</div>
          </div>
        </div>
        <div className="resume__tags">
          {(m.portalRole === 'owner' || m.portalRole === 'admin') && <span className="badge badge--plain">{m.portalRole.toUpperCase()}</span>}
          {m.keyTier === 'key_holder'
            ? <KeyIcon title="Key holder · unlimited keys" />
            : <span className="badge badge--plain">5 KEYS</span>}
        </div>
        {m.vibeCheck && <p className="resume__bio">“{m.vibeCheck}”</p>}
        <div className="resume__stats">
          <div><div className="resume__n">{m.portalsHosted ?? '—'}</div><div className="resume__l">Portals hosted</div></div>
          <div><div className="resume__n">{m.roomsJoined ?? '—'}</div><div className="resume__l">Portals Joined</div></div>
          <div><div className="resume__n">{since}</div><div className="resume__l">Member since</div></div>
        </div>
        <button className="alink" onClick={onOpenFull} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>OPEN FULL PORTAL PAGE →</button>
      </div>
    </div>
  );
}
